import React, {useEffect, useState} from "react";
import {
    SurveyFormAnswerModel,
    SurveyFormChoice,
    SurveyFormEditorNodeType,
    SurveyFormModel,
    SurveyFormNode,
    SurveyFormQuestionModel,
    SurveyFormQuestionType
} from "../model/SurveyFormModel";
import {Grid, Segment} from "semantic-ui-react";
import SurveyFormEditorNavigator from "./editor/SurveyFormEditorNavigator";

import SurveyFormEditorQuestion from "./editor/SurveyFormEditorQuestion";
import {q} from "@fullcalendar/core/internal-common";

interface Props {
    model?: SurveyFormModel,
    readonly?: boolean
}

export function flattenQuestions(list: SurveyFormQuestionModel[]): SurveyFormQuestionModel[] {
    const result: SurveyFormQuestionModel[] = [];
    list.forEach((q) => {
        result.push(q);
        q.answers?.forEach((a) => {
            result.push(...flattenQuestions(a.questions));
        })
    })
    return result;
}


const SurveyFormEditor = (props: Props) => {

    const [questions, setQuestions] = useState(props.model!.questions);
    const [selected, setSelected] = useState<SurveyFormNode | undefined>();

    useEffect(() => {
        setQuestions(props.model?.questions ?? []);
    }, [props.model]);

    useEffect(() => {
        props.model!.questions = questions;
    }, [questions]);

    return (
        <div>
            {props.model && <>
                <Grid>
                    <Grid.Column width={6}>
                        <Segment style={{"height": "570px"}}>
                        <SurveyFormEditorNavigator questions={questions}
                                                   selected={selected}
                                                   readonly={props.readonly}
                                                   onSelectedNodeChanged={setSelected}
                                                   onAddQuestionClick={onAddQuestionClick}
                                                   onAddAnswerClick={onAddAnswerClick}
                                                   onDeleteSelectedClick={onDeleteSelectedClick}
                                                    onUpSelectedClick={onUpSelectedClick}
                                                   onDownSelectedClick={onDownSelectedClick}/>
                        </Segment>

                    </Grid.Column>
                    <Grid.Column width={10} style={{marginTop:"0px"}}>
                        <Segment style={{"height": "570px"}}>
                        {selected && selected.type == SurveyFormEditorNodeType.question &&

                        <SurveyFormEditorQuestion
                            question={selected!.question!}
                            onChanged={onQuestionChanged}
                            readOnly={props.readonly}
                        />}</Segment>
                    </Grid.Column>
                </Grid></>}
        </div>);


    function flattenAnswers(list: SurveyFormQuestionModel[]): SurveyFormAnswerModel[][] {
        const result: SurveyFormAnswerModel[][] = [];
        list.forEach((q) => {
            if (q.answers)
                result.push(q.answers!);
            q.answers?.forEach((a) => {
                result.push(...flattenAnswers(a.questions));
            })
        })
        return result;
    }

    function generateNextQuestionId() {
        const ql = flattenQuestions(questions);
        let n = flattenQuestions(questions).length;
        let result = "q" + (n + 1);
        while (ql.find((x) => x.id == result)) {
            n++;
            result = "q" + (n + 1);
        }
        return result
    }

    function createQuestion(type: SurveyFormQuestionType): SurveyFormQuestionModel {
        let id = generateNextQuestionId();
        let text = "Вопрос";
        switch (type) {
            case SurveyFormQuestionType.bool:
                return {
                    type: SurveyFormQuestionType.bool,
                    questionDisplayText: text,
                    id: id
                };
            case SurveyFormQuestionType.choice:
                return {
                    type: SurveyFormQuestionType.choice,
                    questionDisplayText: text,
                    id: id,
                    multiselect: false,
                    choices: []
                };
            case SurveyFormQuestionType.text:
                return {
                    type: SurveyFormQuestionType.text,
                    questionDisplayText: text,
                    id: id
                };
        }
    }

    function onAddQuestionClick(type: SurveyFormQuestionType) {
        let list =
            selected?.answer ? selected.answer.questions :
                selected?.question ? (getQuestionList(selected!.question!) ?? questions) : questions;
        let question = createQuestion(type);
        list.push(question);
        setQuestions([...questions]);
        setSelected({type: SurveyFormEditorNodeType.question, question: question});
    }

    function onAddAnswerClick(choice: SurveyFormChoice) {
        if (selected?.question) {
            const answer = {choice: choice, questions: []};
            if (!selected.question.answers)
                selected.question.answers = [];
            selected?.question.answers?.push(answer);
            setQuestions([...questions]);
            setSelected({type: SurveyFormEditorNodeType.answer, answer: answer});
        }
    }


    function findQuestionList(question: SurveyFormQuestionModel, list: SurveyFormQuestionModel[]): SurveyFormQuestionModel[] | undefined {
        let result: SurveyFormQuestionModel[] | undefined;
        if (list.find((x) => x == question)) {
            result = list;
        } else {
            list.forEach((q) => {
                if (q.answers && !result) {
                    q.answers?.forEach((answer) => {
                        let subResult = findQuestionList(question, answer.questions);
                        if (subResult)
                            result = subResult;
                    })
                }
            })
        }
        return result;
    }


    function getQuestionList(question: SurveyFormQuestionModel): SurveyFormQuestionModel[] | undefined {
        return findQuestionList(question, questions);
    }

    function getAnswerList(answer: SurveyFormAnswerModel): SurveyFormAnswerModel[] | undefined {
        const list = flattenAnswers(questions);
        for (let i = 0; i < list.length; i++) {
            if (list[i].find((x) => x == answer))
                return list[i];
        }
        return undefined;
    }

    function onDeleteSelectedClick() {
        if (selected && selected.question) {
            let list = getQuestionList(selected.question);
            if (list) {
                let index = list.findIndex((x) => x == selected.question);
                if (index >= 0) {
                    list.splice(index, 1);
                    setQuestions([...questions])
                    setSelected(undefined);
                }
            }
        }
        if (selected && selected.answer) {
            let list = getAnswerList(selected.answer!);
            if (list) {
                let index = list.findIndex((x) => x == selected.answer);
                if (index >= 0) {
                    list.splice(index, 1);
                    setQuestions([...questions])
                    setSelected(undefined);
                }
            }
        }
    }


    function onUpSelectedClick() {
        if (selected && selected.question) {
            let list = getQuestionList(selected.question);
            if (list) {
                let index = list.findIndex((x) => x == selected.question);
                if (index >= 1) {
                    const t = list[index];
                    list[index] = list[index - 1];
                    list[index - 1] = t;
                    setQuestions([...questions])
                }
            }
        }
    }

    function onDownSelectedClick() {
        if (selected && selected.question) {
            let list = getQuestionList(selected.question);
            if (list) {
                let index = list.findIndex((x) => x == selected.question);
                if (index < (list.length - 1)) {
                    const t = list[index];
                    list[index] = list[index + 1];
                    list[index + 1] = t;
                    setQuestions([...questions])
                }
            }
        }
    }


    function onQuestionChanged() {
        setQuestions([...questions])
    }

}

export default SurveyFormEditor;